/**
 * Yvine Design — collections carousel (drift, drag, flick).
 * Cached asset, initialised once per [data-art="carousel"] element by art-core.js
 * (first paint, every in-place page swap, theme-editor reloads).
 */
Art.section('carousel', function (root) {
    var section = root;
    var viewport = section.querySelector('.cc__viewport');
    var track = section.querySelector('.cc__track');
    if (!viewport || !track) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Take over from the CSS marquee so we can drive the position for dragging.
    section.classList.add('cc--js');

    var speedSecs = parseFloat(section.style.getPropertyValue('--cc-speed')) || 45; // seconds per pass
    var pos = 0; //        px translated left (content moves left as pos grows)
    var passWidth = 1; //  width of one list pass = seamless wrap point
    var pxPerMs = 0; //    steady auto-advance speed
    var dragging = false;
    var moved = false; //  crossed the click-cancel threshold this gesture
    var visible = true;
    var startX = 0;
    var startPos = 0;
    var lastMoveX = 0;
    var lastMoveT = 0;
    var velocity = 0; //   px/ms carried into a release flick
    var rafId = null;
    var lastTs = 0;

    function measure() {
      var gap = parseFloat(getComputedStyle(track).columnGap) || 24;
      // The track holds two identical passes; one pass advance = half the track
      // width plus half a gap (mirrors the CSS keyframe end value).
      passWidth = track.offsetWidth / 2 + gap / 2;
      if (!(passWidth > 0)) passWidth = 1;
      pxPerMs = passWidth / (speedSecs * 1000);
    }

    function wrap() {
      pos %= passWidth;
      if (pos < 0) pos += passWidth;
    }

    function apply() {
      track.style.transform = 'translate3d(' + -pos + 'px,0,0)';
    }

    function running() {
      // An in-place page change replaces main, and nothing here is torn down:
      // without this the drift keeps running for ever on a carousel that is no
      // longer in the document, once per visit to the page. `visible` is set
      // by an IntersectionObserver, which is not guaranteed to report a node
      // that was simply removed — so ask the document directly.
      if (!document.contains(root)) return false;
      return visible && (dragging || velocity !== 0 || !reduce);
    }

    function start() {
      if (rafId == null && running()) {
        lastTs = 0;
        rafId = requestAnimationFrame(frame);
      }
    }

    function stop() {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
        lastTs = 0;
      }
    }

    function frame(ts) {
      var dt = lastTs ? Math.min(ts - lastTs, 48) : 16;
      lastTs = ts;

      if (!dragging) {
        if (velocity) {
          pos -= velocity * dt; // release momentum (same sign convention as drag)
          velocity *= Math.pow(0.94, dt / 16); // decay
          if (Math.abs(velocity) < 0.004) velocity = 0;
        }
        if (!reduce) pos += pxPerMs * dt; // steady drift, right -> left
      }
      wrap();
      apply();

      if (running()) rafId = requestAnimationFrame(frame);
      else { rafId = null; lastTs = 0; }
    }

    // ---- Drag ------------------------------------------------------------
    function onDown(e) {
      if (e.button != null && e.button > 0) return; // primary button / touch only
      dragging = true;
      moved = false;
      startX = e.clientX;
      startPos = pos;
      lastMoveX = e.clientX;
      lastMoveT = performance.now();
      velocity = 0;
      section.classList.add('cc--dragging');
      // No setPointerCapture here: capturing retargets the following click to the
      // track, so the card <a> never receives it and nothing navigates. Move/up
      // are tracked on window anyway.
      start();
    }

    function onMove(e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 8) moved = true; // a real drag, not click jitter
      pos = startPos - dx; // drag right -> content follows right
      wrap();
      apply();
      var now = performance.now();
      var mdt = now - lastMoveT;
      if (mdt > 0) velocity = (e.clientX - lastMoveX) / mdt; // px/ms
      lastMoveX = e.clientX;
      lastMoveT = now;
    }

    function onUp() {
      if (!dragging) return;
      dragging = false;
      section.classList.remove('cc--dragging');
      // Cap the flick so a hard throw doesn't rocket away.
      if (velocity > 2.2) velocity = 2.2;
      else if (velocity < -2.2) velocity = -2.2;
      start();
    }

    // Swallow the card link click if the pointer was dragged.
    track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    track.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    // Don't burn frames while the carousel is offscreen or the tab is hidden.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0] && entries[0].isIntersecting;
        if (visible) start();
        else if (!dragging) stop();
      }, { threshold: 0 }).observe(section);
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    window.addEventListener('resize', function onResize() {
      if (!document.contains(root)) { window.removeEventListener('resize', onResize); return; }
      measure();
    }, { passive: true });
    window.addEventListener('load', measure); // re-measure once images/fonts settle
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }

    measure();
    apply();
    start();
});
